/**
 * <Movers> — Top Gainers / Losers (Today's Movers)
 *
 * 오늘 가장 많이 오른/내린 주식. 데일리 시그널, 모멘텀 트레이더 필수.
 * 데이터: GET /api/ticker/market/movers → { gainers: [...], losers: [...] }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import type { WidgetMeta, WidgetProps } from "../types";
import { koreanCompanyName } from "@/lib/korean-company-name";

interface Mover {
  symbol: string;
  name?: string | null;
  price: number | null;
  changePercent: number | null;
  change: number | null;
}

interface MoversData {
  gainers: Mover[];
  losers: Mover[];
}

const REFRESH_MS = 2 * 60 * 1000;

function fmtPct(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(2)}%`;
}

function fmtPrice(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function MoverList({ items, kind }: { items: Mover[]; kind: "up" | "down" }) {
  if (items.length === 0) return <div className="dw-movers-empty">데이터 없음</div>;
  return (
    <div className="dw-movers-list">
      {items.slice(0, 5).map((m) => (
        <Link
          key={m.symbol}
          href={`/analyze/${encodeURIComponent(m.symbol)}`}
          className="dw-movers-row"
          title={`${m.symbol} · ${koreanCompanyName(m.symbol, m.name)}`}
        >
          <span className="dw-movers-name-block">
            <span className="dw-movers-sym">{koreanCompanyName(m.symbol, m.name)}</span>
            <span className="dw-movers-ticker">{m.symbol}</span>
          </span>
          {/* 사용자 결정 #6: price + pct 한 셀 (이름 클램핑 방지) */}
          <span className="dw-movers-pricepct">
            <span className="dw-movers-price">{fmtPrice(m.price)}</span>
            <span className={`dw-movers-pct ${kind}`}>{fmtPct(m.changePercent)}</span>
          </span>
        </Link>
      ))}
    </div>
  );
}

export function Movers(_props: WidgetProps) {
  const [data, setData] = useState<MoversData | null>(null);
  const [tab, setTab] = useState<"gainers" | "losers">("gainers");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/movers", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-movers">
      <div className="dw-head"><span className="dw-title">오늘의 등락</span></div>
      <div className="dw-movers-tabs">
        <button
          type="button"
          className={`dw-movers-tab ${tab === "gainers" ? "is-active up" : ""}`}
          onClick={() => setTab("gainers")}
        >▲ 상승 Top 5</button>
        <button
          type="button"
          className={`dw-movers-tab ${tab === "losers" ? "is-active down" : ""}`}
          onClick={() => setTab("losers")}
        >▼ 하락 Top 5</button>
      </div>
      {error ? (
        <div className="dw-movers-error">{error}</div>
      ) : loading && !data ? (
        <div className="dw-movers-empty">로딩 중…</div>
      ) : tab === "gainers" ? (
        <MoverList items={data?.gainers || []} kind="up" />
      ) : (
        <MoverList items={data?.losers || []} kind="down" />
      )}
    </div>
  );
}

export const moversMeta: WidgetMeta = {
  type: "movers",
  name: "오늘의 등락",
  description: "Top Gainers / Losers · 데일리 시그널",
  icon: "📈",
  category: "discovery",
  defaultSize: { w: 4, h: 5 },
  availableSizes: [
    { w: 3, h: 5 },
    { w: 4, h: 5 },
    { w: 4, h: 6 },
  ],
  minW: 3,
  maxW: 6,
  minH: 4,
  maxH: 7,
  component: Movers,
};
